<template>
  <div
    @focusin="handleFocusIn"
    @focusout="stopHighlighting"
    @keydown.down="next"
    @keydown.up="previous"
    @keyup.enter.exact="selectCurrent"
  >
    <slot />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

export type Action =
  | {
      method: 'next';
      arguments: [KeyboardEvent, { highlight: true; focus: false }];
    }
  | {
      method: 'previous';
      arguments: [KeyboardEvent, { highlight: true; focus: false }];
    }
  | {
      method: 'selectCurrent';
      arguments: [KeyboardEvent, true];
    }
  | {
      method: 'startHighlighting';
      arguments: [];
    }
  | {
      method: 'stopHighlighting';
      arguments: [];
    };

/**
 * Component that wraps an input element (e.g. InputBox) with
 * a div that contains keyboard event listeners that control
 * emit events intended to control a SelectionList
 *
 * A single "action" event with different methods is emitted
 * so the parent component can use a single method
 *
 * The SelectionList isn't passed in as a prop because
 * that is unreliable when slots are used
 *
 * Any other list type component that uses ListNavigation can also
 * be controlled.
 *
 * $emits action
 **/
export default defineComponent({
  name: 'ListController',
  emits: { action: null },

  props: {
    startHighlightOnFocusIn: {
      type: Boolean,
      default: true,
    },
  },

  methods: {
    handleFocusIn(): void {
      if (this.startHighlightOnFocusIn) {
        this.startHighlighting(true);
      }
    },

    next($event: KeyboardEvent) {
      // Prevent scroll
      $event.preventDefault();

      this.$emit('action', {
        method: 'next',
        arguments: [$event, { highlight: true, focus: false }],
      });
    },
    previous($event: KeyboardEvent) {
      // Prevent scroll
      $event.preventDefault();

      this.$emit('action', {
        method: 'previous',
        arguments: [$event, { highlight: true, focus: false }],
      });
    },
    selectCurrent($event: KeyboardEvent) {
      this.$emit('action', {
        method: 'selectCurrent',
        arguments: [$event, true],
      });
    },
    startHighlighting(startHighlighting: boolean) {
      this.$emit('action', {
        method: 'startHighlighting',
        arguments: [startHighlighting],
      });
    },
    stopHighlighting() {
      this.$emit('action', {
        method: 'stopHighlighting',
        arguments: [],
      });
    },
  },
});
</script>
